import {
  BOOKING_PAYMENT_STATUSES,
  BOOKING_SESSION_STATUS,
  BOOKING_STATUSES,
  EVENT_TYPE_CONSULTATION,
  EVENT_TYPE_THERAPY_SESSION,
  getSessionDisplayLabel,
  STATUS_SESSION_CANCELLED,
  SUBTYPE_POST_CONSULTATION,
  SUBTYPE_PRE_CONSULTATION,
  VISIT_EVENT_TYPES,
} from 'src/consumer/constants'
import { Tooltip } from '@mui/material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import StepsCheckUI from 'src/clinic/components/ClinicManageBookingsPage/screening/StepsCheckUI'
import EventDiagnosisChip from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/components/EventDiagnosisChip'

/** Resolve a normalized session status from the various event flags. */
function resolveSessionStatus(bookedSlot) {
  if (
    bookedSlot?.status === STATUS_SESSION_CANCELLED ||
    bookedSlot?.sessionStatus === BOOKING_SESSION_STATUS.CANCELLED
  ) {
    return 'cancelled'
  }
  if (
    bookedSlot?.markedCompleted ||
    bookedSlot?.sessionStatus === BOOKING_SESSION_STATUS.MARKED_COMPLETED ||
    bookedSlot?.sessionStatus === BOOKING_SESSION_STATUS.FINISHED
  ) {
    return 'completed'
  }
  if (bookedSlot?.sessionStatus === BOOKING_SESSION_STATUS.IN_PROGRESS) {
    return 'in-progress'
  }
  return 'scheduled'
}

const SESSION_STATUS_UI = {
  completed: { label: 'Completed', color: '#15803d', bg: 'rgba(22, 163, 74, 0.14)', Icon: CheckCircleRoundedIcon },
  'in-progress': { label: 'In progress', color: '#b45309', bg: 'rgba(245, 158, 11, 0.16)', Icon: PlayCircleFilledRoundedIcon },
  cancelled: { label: 'Cancelled', color: '#b91c1c', bg: 'rgba(220, 38, 38, 0.12)', Icon: CancelRoundedIcon },
  scheduled: { label: 'Scheduled', color: '#475569', bg: 'rgba(100, 116, 139, 0.12)', Icon: ScheduleRoundedIcon },
}

/** Clear, icon-led session status pill (Completed is unmistakable). */
function SessionStatusChip({ bookedSlot }) {
  const status = resolveSessionStatus(bookedSlot)
  const ui = SESSION_STATUS_UI[status]
  const Icon = ui.Icon
  return (
    <Tooltip title={`Session: ${ui.label}`} arrow>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '11px',
          fontWeight: 700,
          color: ui.color,
          backgroundColor: ui.bg,
          padding: '1px 7px 1px 5px',
          borderRadius: '999px',
          lineHeight: 1.5,
          whiteSpace: 'nowrap',
        }}
      >
        <Icon style={{ fontSize: 14 }} />
        {ui.label}
      </span>
    </Tooltip>
  )
}

function StatusBadge({ color, background, children }) {
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 700,
        color,
        backgroundColor: background,
        padding: '2px 6px',
        borderRadius: '4px',
        whiteSpace: 'nowrap',
        lineHeight: 1.4,
      }}
    >
      {children}
    </span>
  )
}

/**
 * Surfaces the same event-type / screening / status details shown on the calendar
 * strip, for an Appointments row: the session label, screening + step statuses
 * (reused from StepsCheckUI) and the strip's status badges.
 */
export default function ConsultsDetailsCell({ bookedSlot }) {
  const label = getSessionDisplayLabel(bookedSlot)
  const title = bookedSlot?.title

  const isEmergency = bookedSlot?.visitEventType === VISIT_EVENT_TYPES.emergency
  const isCancelled = bookedSlot?.status === STATUS_SESSION_CANCELLED
  const isPaymentPending =
    bookedSlot?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
    bookedSlot?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING
  const isPaidOnAumhum = bookedSlot?.feePaymentMode === 'aumhum'
  const isPreRx = bookedSlot?.eventSubType === SUBTYPE_PRE_CONSULTATION
  const isPostRx = bookedSlot?.eventSubType === SUBTYPE_POST_CONSULTATION
  const isRecording = Boolean(bookedSlot?.recordingEnabled)
  const wasDelayed = Boolean(bookedSlot?.wasDelayedBecauseOfOverlaps)

  const hasAnyBadge =
    isEmergency ||
    isCancelled ||
    isPaymentPending ||
    isPaidOnAumhum ||
    isPreRx ||
    isPostRx ||
    isRecording ||
    wasDelayed

  return (
    <div className="d-flex flex-column gap-1" style={{ minWidth: '180px' }}>
      <div className="d-flex align-items-center gap-1 flex-wrap">
        <SessionStatusChip bookedSlot={bookedSlot} />
          <span style={{ fontWeight: 600 }}>{label}</span>
          {title && title !== label && (
            <span style={{ color: '#6b7280', fontSize: '12px' }}>· {title}</span>
          )}
        </div>

        {hasAnyBadge && (
          <div className="d-flex align-items-center gap-1 flex-wrap">
            {isEmergency && (
              <StatusBadge color="#ffffff" background="#dc2626">
                🚨 Emergency
              </StatusBadge>
            )}
            {isPaymentPending && (
              <StatusBadge color="#7c2d12" background="rgba(251, 146, 60, 0.2)">
                Payment pending
              </StatusBadge>
            )}
            {isCancelled && (
              <StatusBadge color="#dc2626" background="rgba(220, 38, 38, 0.15)">
                Cancelled
              </StatusBadge>
            )}
            {isPreRx && (
              <StatusBadge color="#6366f1" background="rgba(99, 102, 241, 0.15)">
                Pre-Rx
              </StatusBadge>
            )}
            {isPostRx && (
              <StatusBadge color="#8b5cf6" background="rgba(139, 92, 246, 0.15)">
                Post-Rx
              </StatusBadge>
            )}
            {isPaidOnAumhum && (
              <StatusBadge color="#f59e0b" background="rgba(245, 158, 11, 0.15)">
                Paid on AUMHUM
              </StatusBadge>
            )}
            {isRecording && (
              <StatusBadge color="#dc2626" background="rgba(220, 38, 38, 0.12)">
                ● Rec
              </StatusBadge>
            )}
            {wasDelayed && (
              <StatusBadge color="#92400e" background="rgba(245, 158, 11, 0.15)">
                ⏰ Shifted
              </StatusBadge>
            )}
          </div>
        )}

        {/* Short clinical label for completed appointments — Dx/Cc for consultations, Tx for
            therapy sessions (lazy-loaded + cached; self-gates). */}
        {(bookedSlot?.eventType === EVENT_TYPE_CONSULTATION ||
          bookedSlot?.eventType === EVENT_TYPE_THERAPY_SESSION) && (
          <EventDiagnosisChip bookedSlot={bookedSlot} sessionStatus={resolveSessionStatus(bookedSlot)} />
        )}

        {/* Screening / therapy step statuses (vitals shown in their own column) */}
        <StepsCheckUI bookedSlot={bookedSlot} hideVitals />
    </div>
  )
}
